/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {intersection} from "lodash";
import EmailStep from "../models/emailStepModel";
import {CustomerType, Fulfilment} from "./interfaces";

/*
Validation steps:
1. No more than 1 mail per customer (based on combination of customer type, selected products and countries)
2. Has got invoice as attachment
 */

export function validateEmailSteps(steps: EmailStep[]): string | null {
    for (const step of steps) {
        const error = validateStep(step, steps);
        if (error) return error;
    }
    return null;
}

export function validateStep(step: EmailStep, steps: EmailStep[]): string | null {
    const {customerType, productEans, countries, fulfilment} = step;
    for (const otherStep of steps) {
        if (otherStep === step) continue;
        const customerIsValid = validateCustomerType(customerType, otherStep.customerType);
        const productsAreValid = validateProducts(productEans, otherStep.productEans);
        const countriesAreValid = validateCountries(countries, otherStep.countries);
        const fulfilmentIsValid = validateFulfilment(fulfilment, otherStep.fulfilment);
        if (!customerIsValid && !productsAreValid && !countriesAreValid && !fulfilmentIsValid)
            return 'duplicate_mail';
    }
    if (!step.withInvoice) return 'invoice_attachment';
    return null;
}

function validateCountries(countries: string[], otherCountries: string[]): boolean | null {
    if (!countries.length || !otherCountries.length) return false;
    return !intersection(countries, otherCountries).length;
}

function validateCustomerType(customerType: CustomerType, otherCustomerType: CustomerType): boolean {
    if (customerType === CustomerType.BOTH || otherCustomerType === CustomerType.BOTH) return false;
    return customerType !== otherCustomerType;
}

function validateProducts(products: string[], otherProducts: string[]) {
    if (!products.length || !otherProducts.length) return false;
    if (products.includes('all')) return products.length > 1;
    if (otherProducts.includes('all')) return otherProducts.length > 1;
    return !intersection(products, otherProducts).length;
}

function validateFulfilment(fulfilment: Fulfilment, otherFulfilment: Fulfilment): boolean {
    if (fulfilment === Fulfilment.BOTH || otherFulfilment === Fulfilment.BOTH) return false;
    return fulfilment !== otherFulfilment;
}

export function translateEmailStepError(error: string | null | undefined, generalError: boolean): string | null {
    if (!error) return null;
    if (error === 'invoice_attachment')
        if (generalError) return 'Eén of meer e-mails bevat niet de factuur als bijlage, volgens het beleid van ' +
            'Bol moet de factuur als bijlage meegezonden worden. Het kan zijn dat Bol deze e-mails blokkeert en je ' +
            'loopt het risico op beleidspuntenaftrek.';
        else return 'Deze e-mail bevat niet de factuur als bijlage.'
    else if (error === 'duplicate_mail')
        if (generalError) return 'Er worden meerdere e-mails naar dezelfde klanten verzonden, volgens het beleid ' +
            'van Bol mag iedere klant maar één e-mail ontvangen. Het kan zijn dat Bol deze e-mails blokkeert en je ' +
            'loopt het risico op beleidspuntenaftrek.'
        else return 'Er wordt al een andere e-mail naar deze klanten verzonden. Stuur maximaal 1 mail per 1 order.'
    return null;
}